import axios, { AxiosError } from 'axios';
import { Api } from '../axios-config';
import { Environment } from '../../../environment';
import { comOffline, somenteOnline } from '../../../offline/servico';
import {
  caixasLocais, guardarCaixas, guardarStatusCaixa, statusLocalCaixa, statusPendenteCaixa,
} from '../../../offline/sessaoOffline';

export type TCaixaTurnoStatus =
  | { idconta: string; nomeoperador: string; nomeConta?: string; aberto: true; dataabertura: string; valorabertura: number }
  | { idconta: string; nomeoperador: string; nomeConta?: string; aberto: false };

export interface ICaixaAbertura {
  idconta: string;
  dataabertura: string;
  valorabertura: number;
}

export interface IFormaPagamentoAbertura {
  valor: number;
  categoria: string;
}

export interface ICaixaFechamento {
  idconta: string;
  datafechamento: string;
  valorfechamento: number;
  idcontadestino: string;
}

export interface ICaixaMovimento {
  datalan: string;
  numero: string;
  nome?: string;
  historico: string;
  entrada: number;
  saida: number;
  tipo: number;
  status?: boolean;
  logo?: string;
}

export interface IRecebimentoPorForma {
  categoria: string;
  qtde: number;
  valor: number;
}

export interface IPagamentoDespesa {
  descricao: string;
  categoria: string;
  formaPagamento: string;
  valor: number;
  data: string;
}

export interface ICaixaFluxoResumo {
  saldoanterior: number;
  credito: number;
  debito: number;
  saldo: number;
  saldoreal: number;
  porFormaPagamento: { categoria: string; valor: number }[];
  // Saldo de TODO o caixa por forma (inclui sessões anteriores) — o
  // disponível pra sangria. Opcional: backend antigo não manda.
  saldoRealPorForma?: { categoria: string; valor: number }[];
  sangrias: number;
  recebimentosPorForma: IRecebimentoPorForma[];
  pagamentosEDespesas: IPagamentoDespesa[];
}

export interface ICaixaPdvItem {
  idconta: string;
  titular: string;
  aberto: boolean;
  dataabertura?: string;
  valorabertura?: number;
  // Quem está com a sessão aberta nesse caixa agora (só nos tenants com
  // sessão — ver ListarCaixasPdv.ts no backend).
  nomeOperador?: string;
  // Dono da sessão aberta nesse caixa — só ele pode entrar nesse caixa no
  // PDV (ver DialogSelecionarCaixa e a checagem ao abrir o PDV em Pdv.tsx).
  iduserSessao?: string;
  // Conciliação já fechou o dia de hoje pra esse caixa — mesmo sem sessão
  // aberta (aberto: false), tentar abrir uma sessão hoje é recusado pelo
  // backend (ver contaFechadaHojePorConciliacao/abrirSessaoTransferencia).
  // DialogSelecionarCaixa usa isso pra bloquear o clique em vez de deixar o
  // operador preencher o valor de abertura pra só então tomar o erro.
  fechadoHoje?: boolean;
}

export type TTipoSangria = 'cofre' | 'pagamento';

export interface IFormaPagamentoSangria {
  categoria: string;
  valor: number;
}

export interface ICaixaSangria {
  idconta: string;
  valor: number;
  datalan: string;
  historico: string;
  numero: number;
}

export interface ICaixaTransferencia {
  idcontaOrigem: string;
  idcontaDestino: string;
  valor: number;
  datalan: string;
  historico: string;
  numero: number;
}

interface ErrorResponse {
  errors?: { default: string };
  error?: { default: string };
}

const extractError = (error: unknown): Error => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ErrorResponse>;
    const message = axiosError.response?.data?.errors?.default || axiosError.response?.data?.error?.default;
    if (message) return new Error(message);
    if (axiosError.request) return new Error('Nenhuma resposta do servidor');
    return new Error(axiosError.message);
  }
  return new Error('Erro inesperado');
};

const listarCaixasOnline = async (): Promise<ICaixaPdvItem[] | Error> => {
  try {
    const { data } = await Api.get('/caixaturno/caixas');
    return data;
  } catch (error) {
    return extractError(error);
  }
};

const getStatusOnline = async (): Promise<TCaixaTurnoStatus | Error> => {
  try {
    const { data } = await Api.get('/caixaturno/status');
    return data;
  } catch (error) {
    return extractError(error);
  }
};

const getFluxoOnline = async (
  datainicial: string,
  datafinal: string,
  tipo = '',
): Promise<[ICaixaFluxoResumo, ICaixaMovimento[]] | Error> => {
  try {
    const { data } = await Api.get('/caixaturno/fluxo', { params: { datainicial, datafinal, tipo } });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

const sangriaOnline = async (
  tipo: TTipoSangria,
  formasPagamento: IFormaPagamentoSangria[],
  motivo = '',
): Promise<ICaixaSangria | Error> => {
  try {
    const { data } = await Api.post('/caixaturno/sangria', {
      tipo,
      formasPagamento,
      motivo,
      idparceiro: Environment.URL_IDPARCEIRO,
      idplanoconta: Environment.URL_IDPLANODECONTAS,
    });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

// Transferência de dinheiro do caixa do operador logado pro caixa de outro
// operador (ou qualquer conta escolhida) — só em Dinheiro, validado no
// backend contra o que o caixa atual realmente tem.
const transferirOnline = async (idcontadestino: string, valor: number, motivo = ''): Promise<ICaixaTransferencia | Error> => {
  try {
    const { data } = await Api.post('/caixaturno/transferencia', { idcontadestino, valor, motivo });
    return data;
  } catch (error) {
    return extractError(error);
  }
};

// ---------- Offline (ver shared/offline/sessaoOffline.ts) ----------

const listarCaixas = (): Promise<ICaixaPdvItem[] | Error> =>
  comOffline(listarCaixasOnline, caixasLocais, guardarCaixas);

// Caixa aberto offline e ainda não sincronizado tem prioridade sobre o
// servidor (ver SessaoCaixaService.getAtiva).
const getStatus = async (): Promise<TCaixaTurnoStatus | Error> => {
  const idconta = String(Api.defaults.headers.idconta || '');
  const pendente = await statusPendenteCaixa(idconta);
  if (pendente) return pendente;
  return comOffline<TCaixaTurnoStatus>(getStatusOnline, () => statusLocalCaixa(idconta), guardarStatusCaixa);
};

// Dependem do saldo real no servidor — só com internet.
const getFluxo = (datainicial: string, datafinal: string, tipo = '') =>
  somenteOnline(() => getFluxoOnline(datainicial, datafinal, tipo));
const sangria = (tipo: TTipoSangria, formasPagamento: IFormaPagamentoSangria[], motivo = '') =>
  somenteOnline(() => sangriaOnline(tipo, formasPagamento, motivo));
const transferir = (idcontadestino: string, valor: number, motivo = '') =>
  somenteOnline(() => transferirOnline(idcontadestino, valor, motivo));

export const CaixaTurnoService = {
  listarCaixas,
  getStatus,
  getFluxo,
  sangria,
  transferir,
};
